import { readonly, shallowRef } from "vue";
import type { Ref } from "vue";
import type { RouteLocationNormalized, Router } from "vue-router";

const SHOW_DELAY_MS = 120;
const COMPLETE_DELAY_MS = 180;
const PROGRESS_INTERVAL_MS = 180;
const MAX_SIMULATED_PROGRESS = 85;

const pendingRoute = shallowRef<RouteLocationNormalized | null>(null);
const isNavigationPending = shallowRef(false);
const progress = shallowRef(0);
let showTimer: ReturnType<typeof setTimeout> | undefined;
let completeTimer: ReturnType<typeof setTimeout> | undefined;
let progressTimer: ReturnType<typeof setInterval> | undefined;

/** Public state for the application shell; route details stay private. */
export const navigationPending: Readonly<Ref<boolean>> = readonly(isNavigationPending);
export const navigationProgress: Readonly<Ref<number>> = readonly(progress);

const clearTimers = () => {
  if (showTimer) clearTimeout(showTimer);
  if (completeTimer) clearTimeout(completeTimer);
  if (progressTimer) clearInterval(progressTimer);
  showTimer = undefined;
  completeTimer = undefined;
  progressTimer = undefined;
};

export function trackNavigationProgress(router: Router) {
  router.beforeEach((to) => {
    pendingRoute.value = to;
    clearTimers();
    isNavigationPending.value = false;
    progress.value = 0;

    showTimer = setTimeout(() => {
      if (pendingRoute.value !== to) return;

      isNavigationPending.value = true;
      progress.value = 12;
      progressTimer = setInterval(() => {
        progress.value = Math.min(
          MAX_SIMULATED_PROGRESS,
          progress.value + Math.max(1, (MAX_SIMULATED_PROGRESS - progress.value) * 0.14),
        );
      }, PROGRESS_INTERVAL_MS);
    }, SHOW_DELAY_MS);
  });
  const finish = (to: RouteLocationNormalized) => {
    if (pendingRoute.value === to) {
      pendingRoute.value = null;
      if (showTimer) clearTimeout(showTimer);
      if (progressTimer) clearInterval(progressTimer);
      showTimer = undefined;
      progressTimer = undefined;

      if (!isNavigationPending.value) {
        progress.value = 0;
        return;
      }

      progress.value = 100;
      completeTimer = setTimeout(() => {
        isNavigationPending.value = false;
        progress.value = 0;
        completeTimer = undefined;
      }, COMPLETE_DELAY_MS);
    }
  };
  router.afterEach(finish);
  router.onError((_error, to) => finish(to));
}
